import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const STATUSES = ["Planning", "Registrations Open", "Confirmed", "Live", "Completed", "Cancelled"];
const TYPES = ["Football Tournament", "Basketball Tournament", "Pickleball Tournament", "Corporate Event", "Community Event", "Sports Festival", "Academy Event", "Other"];

const EventDialog = ({ venues, users, onSaved, trigger }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    name: "", type: "Football Tournament", venue_id: "", date: "", start_time: "", end_time: "",
    owner_id: "", status: "Planning", expected_participants: 0, expected_footfall: 0,
    registration_fee: 0, revenue_target: 0, budget: 0, actual_revenue: 0, actual_expense: 0, notes: "",
  });
  const save = async () => {
    if (!f.name.trim() || !f.venue_id) return toast.error("Name and venue required");
    const v = venues.find((x) => x.id === f.venue_id);
    const o = users.find((x) => x.id === f.owner_id);
    try {
      await api.post("/events", { ...f, venue_name: v?.name, owner_name: o?.name || "" });
      toast.success("Event created"); setOpen(false); onSaved && onSaved();
    } catch { toast.error("Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New Event</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input data-testid="event-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Type</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Venue</Label><Select value={f.venue_id} onValueChange={(v) => setF({ ...f, venue_id: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
          <div><Label>Status</Label><Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Start Time</Label><Input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
          <div><Label>End Time</Label><Input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} /></div>
          <div><Label>Owner</Label><Select value={f.owner_id} onValueChange={(v) => setF({ ...f, owner_id: v })}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Expected Participants</Label><Input type="number" value={f.expected_participants} onChange={(e) => setF({ ...f, expected_participants: +e.target.value })} /></div>
          <div><Label>Expected Footfall</Label><Input type="number" value={f.expected_footfall} onChange={(e) => setF({ ...f, expected_footfall: +e.target.value })} /></div>
          <div><Label>Reg. Fee (₹)</Label><Input type="number" value={f.registration_fee} onChange={(e) => setF({ ...f, registration_fee: +e.target.value })} /></div>
          <div><Label>Revenue Target (₹)</Label><Input type="number" value={f.revenue_target} onChange={(e) => setF({ ...f, revenue_target: +e.target.value })} /></div>
          <div><Label>Budget (₹)</Label><Input type="number" value={f.budget} onChange={(e) => setF({ ...f, budget: +e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="event-save-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [users, setUsers] = useState([]);
  const load = async () => {
    const [e, v, u] = await Promise.all([api.get("/events"), api.get("/venues"), api.get("/users")]);
    setEvents(e.data); setVenues(v.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Events</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>All Events</h1>
        </div>
        <EventDialog venues={venues} users={users} onSaved={load} trigger={<Button data-testid="new-event-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New Event</Button>} />
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.length === 0 && <div className="text-slate-500 text-sm">No events yet.</div>}
        {events.map((e) => (
          <Link key={e.id} to={`/events/${e.id}`} data-testid={`event-card-${e.id}`} className="block bg-white border border-slate-200 rounded-md p-5 hover:shadow-md hover:-translate-y-0.5 transition-transform">
            <div className="flex justify-between"><div className="text-lg font-bold text-slate-900">{e.name}</div><StatusBadge status={e.status} /></div>
            <div className="text-sm text-slate-500 mt-1">{e.type}</div>
            <div className="text-sm text-slate-500">{e.venue_name} · {fmtDate(e.date)}</div>
            <div className="mt-3 flex gap-4 text-xs">
              <div><span className="text-slate-500">Target</span><div className="font-mono font-semibold">{fmtINR(e.revenue_target)}</div></div>
              <div><span className="text-slate-500">Budget</span><div className="font-mono font-semibold">{fmtINR(e.budget)}</div></div>
              <div><span className="text-slate-500">Owner</span><div className="font-semibold">{e.owner_name || "—"}</div></div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
